import { getProductDashboardLink, PAGE_LINKS } from '@/router/pageConst'
import { userService, useUserInfo } from '@/services/userService'
import { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styles from './HomePagePc.module.less'
import cls from 'classnames'
import InviteLinkStatusDialog from '../InviteLinkStatusDialog/InviteLinkStatusDialog'
import { commonUtils } from '@/helpers/commonUtils'
import AIcon from '@/components/A/AIcon/AIcon'
import MiddleText from '@/components/MiddleText/MiddleText'
import DemoDrawer from '../DemoDrawer/DemoDrawer'
import CookieAcceptNotification from '@/components/CookieAcceptNotification/CookieAcceptNotification'
import { observer } from 'mobx-react'
import { INTERNAL_HOST } from '@/commonConst'
import NiceModal from '@ebay/nice-modal-react'
import AigcPromotionModal from '@/components/Modals/AigcPromotionModal/AigcPromotionModal'
import AigcPromoEntry from '../AigcPromoEntry/AigcPromoEntry'
import { useTypedLocalStorage } from '@/helpers/commonHooks/useTypedLocalStorage'
import ProductSwitchModal from '@/components/Modals/ProductSwitchModal/ProductSwitchModal'
import { api } from '@/api'


const StartBtn = observer(() => {
  const { t } = useTranslation()
  const [hover, setHover] = useState(false)
  const navigate = useNavigate()
  return (
    <button
      data-testid='home-start-btn'
      className={cls(styles.startBtn, { [styles.hover]: hover })}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={async () => {
        if (userService.userInfo) {
          try {
            const entryData = await api.corp.analysisCntEntry().send()
            NiceModal.show(ProductSwitchModal, {
              productType: 'horizontal-base',
              entryData,
              onSelectProduct: (productType: ProjectModels.ProjectCategory) => {
                navigate(getProductDashboardLink(productType, 'project'))
              },
            })
          } catch {
            navigate(PAGE_LINKS.project)
          }
        } else {
          navigate(PAGE_LINKS.login)
        }
      }}
    >
      <span>{t('landing.startBtn')}</span>
      {hover ? (
        <img src={commonUtils.getImagePath('homePage/right-arrow-icon-white.png')} alt='' />
      ) : (
        <img src={commonUtils.getImagePath('homePage/right-arrow-icon-black.png')} alt='' />
      )}
    </button>
  )
})


function HomePagePc() {
  const { t, i18n } = useTranslation()
  const isJaLocale = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('ja')
  const userInfo = useUserInfo()
  const [advanceIndex, setAdvanceIndex] = useState(1)
  const [open, setOpen] = useState<boolean>(false)
  const [showAigcEntry, setShowAigcEntry] = useState(false)
  const [, setIsFrom] = useTypedLocalStorage('is_from', { defaultValue: '' })
  const navigate = useNavigate()

  const handleAdvanceMouseOver = useCallback((index: number) => {
    setAdvanceIndex(index)
  }, [])

  const handleCreatePage = async () => {
    if (userService.userInfo && !userService.userInfo.isExpert) {
      try {
        const entryData = await api.corp.analysisCntEntry().send()
        NiceModal.show(ProductSwitchModal, {
          productType: 'horizontal-base',
          entryData,
          onSelectProduct: (productType: ProjectModels.ProjectCategory) => {
            navigate(getProductDashboardLink(productType, 'project'))
          },
        })
      } catch {
        navigate(PAGE_LINKS.project)
      }
    } else {
      navigate(PAGE_LINKS.login)
    }
  }

  const openAigcPromotionModal = useCallback(() => {
    setShowAigcEntry(false)
    NiceModal.show(AigcPromotionModal, {
      onRegister: () => {
        setIsFrom('aigc')
        navigate(PAGE_LINKS.login, { state: { mode: 'sign' } })
      },
      onAfterClose: () => {
        if (userInfo === null) {
          setShowAigcEntry(true)
        }
      },
    })
  }, [navigate, userInfo])

  useEffect(() => {
    if (userInfo === undefined) {
      return
    }
    setShowAigcEntry(userInfo === null)
  }, [userInfo])

  return (
    <div className={styles.homePage} data-testid='home-page'>
      <InviteLinkStatusDialog />
      <div className={styles.bannerBox}>
        <div className={cls(styles.bannerContent, styles[i18n.language])}>
          <img
            className={styles.bannerCover}
            src={
              i18n.language === 'zh'
                ? commonUtils.getImagePath('homePage/banner-cover-zh.png')
                : commonUtils.getImagePath('homePage/banner-cover-en.png')
            }
            alt=''
          />
          <div className={styles.titleBox}>
            <img
              className={cls(styles.aiLine, styles[i18n.language])}
              src={commonUtils.getImagePath('homePage/ai-line.png')}
              alt=''
            />
            <span className={cls(styles.highlight, styles.text, styles[i18n.language])}>
              {t('landing.banner.text1')}
            </span>
            <span className={cls(styles.text, styles[i18n.language])}>
              {t('landing.banner.text2')}
            </span>
            <span className={cls(styles.text, styles[i18n.language])}>
              {t('landing.banner.text3')}
            </span>
          </div>
          <div className={styles.descBox}>{t('landing.banner.text4')}</div>
          {/** NOTE: 这个空 div 是因为里面的样式是 inline-flex ，外部需要这个空 div 换行 */}
          <div>
            <div className={cls(styles.featureBox, styles.first, styles[i18n.language])}>
              <div className={styles.feature}>
                <img
                  className={styles.featureIcon}
                  src={commonUtils.getImagePath('homePage/camera-icon.png')}
                  alt=''
                />
                <span>{t('landing.banner.feature1')}</span>
              </div>
              <div className={styles.feature}>
                <img
                  className={styles.featureIcon}
                  src={commonUtils.getImagePath('homePage/analysis-icon.png')}
                  alt=''
                />
                <span>{t('landing.banner.feature2')}</span>
              </div>
            </div>
          </div>
          {/** NOTE: 这个空 div 是因为里面的样式是 inline-flex ，外部需要这个空 div 换行 */}
          <div>
            <div className={styles.featureBox}>
              <div className={styles.feature}>
                <img
                  className={styles.featureIcon}
                  src={commonUtils.getImagePath('homePage/emotion-icon.png')}
                  alt=''
                />
                <span>{t('landing.banner.feature3')}</span>
              </div>
              <div className={cls(styles.feature, styles.heat)}>
                <img
                  className={styles.featureIcon}
                  src={commonUtils.getImagePath('homePage/heat-icon.png')}
                  alt=''
                />
                <span>{t('landing.banner.feature4')}</span>
              </div>
            </div>
          </div>
          <div className={styles.btnBox}>
            {/** 隐藏 getDemo 的按钮 */}
            <button
              className={cls(styles.btn, styles.left)}
              style={{ display: 'none' }}
              onClick={() => {
                setOpen(true)
              }}
            >
              <span>{t('landing.banner.btn1')}</span>
              <img src={commonUtils.getImagePath('homePage/banner-btn-left-icon.png')} alt='' />
            </button>
            <button className={cls(styles.btn, styles.left)} onClick={handleCreatePage} data-testid='home-hero-cta-btn'>
              <AIcon type='icon-line-global_add' />
              <MiddleText text={t('landing.startBtn')} />
            </button>
          </div>
        </div>
      </div>
      <div className={styles.infoBox}>
        <div className={styles.infoContent}>
          <div className={cls(styles.infoTitle, { [styles.ja]: isJaLocale })}>
            {isJaLocale ? (
              <>
                <span className={styles.titleLine}>超グラフ・マルチモーダル大規模言語モデル</span>
                <span className={styles.titleLine}>（HMLLM）*に基づき、クリエイティブの効果を</span>
                <span className={styles.titleLine}>素早く予測し、広告の投資対効果（ROI）を向上させます。</span>
              </>
            ) : (
              t('landing.info.title')
            )}
          </div>
          <p className={styles.infoSubTitle}>{t('landing.info.subTitle')}</p>
          <img
            className={styles.infoBg}
            src={commonUtils.getImagePath('homePage/info-bg.png')}
            alt=''
          />
          <img
            className={styles.infoPic}
            src={commonUtils.getImagePath(
              i18n.language === 'zh' ? 'homePage/info-pic-zh.png' : 'homePage/info-pic-en.png'
            )}
            alt=''
          />
          <div className={styles.infoDetail}>
            <div className={styles.detailBox}>
              <div className={styles.detailTitle}>
                <span className={styles.block}></span>
                <span>{t('landing.info.detail1')}</span>
              </div>
              <div className={cls(styles.detailDescBox)}>
                <span className={styles.detailDesc}>{t('landing.info.detail1Desc')}</span>
              </div>
              <div className={cls(styles.detailBtnBox)}>
                <StartBtn />
              </div>
            </div>
            <img
              className={styles.infoIntroPic}
              src={commonUtils.getImagePath(
                i18n.language === 'zh'
                  ? 'homePage/info-replace-pic-1-zh.png'
                  : 'homePage/info-replace-pic-1-en.png'
              )}
              alt=''
            />
          </div>
          <div className={cls(styles.infoDetail, styles.reverse)}>
            <img
              className={cls(styles.colorfulBall)}
              src={commonUtils.getImagePath('homePage/colorful-ball.png')}
              alt=''
            />
            <div className={cls(styles.detailBox, styles.reverse, styles[i18n.language])}>
              <div className={cls(styles.detailTitle, styles.reverse)}>
                <span className={cls(styles.block, styles.reverse)}></span>
                <span className={cls(styles.titleText, styles.reverse)}>
                  {t('landing.info.detail2')}
                </span>
              </div>
              <div className={cls(styles.detailDescBox, styles.reverse)}>
                <span className={cls(styles.detailDesc, styles.reverse)}>
                  {t('landing.info.detail2Desc')}
                </span>
              </div>
              <div className={cls(styles.detailBtnBox, styles.reverse)}>
                <StartBtn />
              </div>
            </div>
            <img
              className={styles.infoIntroPic}
              src={commonUtils.getImagePath(
                i18n.language === 'zh'
                  ? 'homePage/info-replace-pic-2-zh.png'
                  : 'homePage/info-replace-pic-2-en.png'
              )}
              alt=''
            />
          </div>
          <div className={styles.infoDetail}>
            <div className={styles.detailBox}>
              <div className={styles.detailTitle}>
                <span className={styles.block}></span>
                <span>
                  {isJaLocale ? (
                    <>
                      <span className={styles.titleLine}>ワンクリック編集で、</span>
                      <span className={styles.titleLine}>効果的な多彩な</span>
                      <span className={styles.titleLine}>バリエーションを生成</span>
                    </>
                  ) : (
                    t('landing.info.detail3')
                  )}
                </span>
              </div>
              <div className={styles.detailDescBox}>
                <span className={styles.detailDesc}>{t('landing.info.detail3Desc')}</span>
              </div>
              <div className={styles.detailBtnBox}>
                <StartBtn />
              </div>
            </div>
            <img
              className={styles.infoIntroPic}
              src={commonUtils.getImagePath(
                i18n.language === 'zh'
                  ? 'homePage/info-replace-pic-3-zh.png'
                  : 'homePage/info-replace-pic-3-en.png'
              )}
              alt=''
            />
          </div>
        </div>
      </div>
      <div className={styles.advanceBox}>
        <div className={styles.advanceBoxContent}>
          <p className={styles.advanceTitle}>{t('landing.advantages.title')}</p>
          <div
            className={cls(styles.advanceItem, styles[i18n.language], styles.one, {
              [styles.active]: advanceIndex === 1,
            })}
            onMouseOver={() => handleAdvanceMouseOver(1)}
          >
            <div className={cls(styles.itemPicBox, styles.one)}></div>
            <p className={styles.itemTitle}>{t('landing.advantages.item1Title')}</p>
            <p className={styles.itemDesc}>
              <Trans
                i18nKey='landing.advantages.item1Desc'
                components={{ tmp: <span className={styles.descBold} /> }}
              />
            </p>
          </div>
          <div
            className={cls(styles.advanceItem, styles[i18n.language], styles.two, {
              [styles.active]: advanceIndex === 2,
            })}
            onMouseOver={() => handleAdvanceMouseOver(2)}
          >
            <div className={cls(styles.itemPicBox, styles.two)}></div>
            <p className={styles.itemTitle}>{t('landing.advantages.item2Title')}</p>
            <p className={styles.itemDesc}>
              <Trans
                i18nKey='landing.advantages.item2Desc'
                components={{ tmp: <span className={styles.descBold} /> }}
              />
            </p>
          </div>
          <div
            className={cls(styles.advanceItem, styles[i18n.language], styles.three, {
              [styles.active]: advanceIndex === 3,
            })}
            onMouseOver={() => handleAdvanceMouseOver(3)}
          >
            <div className={cls(styles.itemPicBox, styles.three)}></div>
            <p className={styles.itemTitle}>{t('landing.advantages.item3Title')}</p>
            <p className={styles.itemDesc}>
              <Trans
                i18nKey='landing.advantages.item3Desc'
                components={{ tmp: <span className={styles.descBold} /> }}
              />
            </p>
          </div>
          <div
            className={cls(styles.advanceItem, styles[i18n.language], styles.four, {
              [styles.active]: advanceIndex === 4,
            })}
            onMouseOver={() => handleAdvanceMouseOver(4)}
          >
            <div className={cls(styles.itemPicBox, styles.four)}></div>
            <p className={styles.itemTitle}>{t('landing.advantages.item4Title')}</p>
            <p className={styles.itemDesc}>
              <Trans
                i18nKey='landing.advantages.item4Desc'
                components={{ tmp: <span className={styles.descBold} /> }}
              />
            </p>
          </div>
          <div
            className={cls(styles.advanceItem, styles[i18n.language], styles.five, {
              [styles.active]: advanceIndex === 5,
            })}
            onMouseOver={() => handleAdvanceMouseOver(5)}
          >
            <div className={cls(styles.itemPicBox, styles.five)}></div>
            <p className={styles.itemTitle}>{t('landing.advantages.item5Title')}</p>
            <p className={styles.itemDesc}>
              <Trans
                i18nKey='landing.advantages.item5Desc'
                components={{ tmp: <span className={styles.descBold} /> }}
              />
            </p>
          </div>
        </div>
      </div>
      <div className={styles.serviceBox}>
        <p className={styles.serviceTitle}>{t('landing.service.title')}</p>
        <p className={styles.serviceDesc}>{t('landing.service.desc')}</p>
        <div className={styles.serviceBoxContent}>
          <img
            className={styles.serviceBottomLeftBg}
            src={commonUtils.getImagePath('homePage/service-bottom-left-bg.png')}
            alt=''
          />
          <img
            className={styles.serviceBottomRightBg}
            src={commonUtils.getImagePath('homePage/service-bottom-right-bg.png')}
            alt=''
          />
          <div className={styles.top}>
            <div className={styles.topTitle}>
              <img src={commonUtils.getImagePath('homePage/service-top-title.png')} alt='' />
              <span>{t('landing.service.topTitle')}</span>
            </div>
            <div className={styles.topRow}>
              <div className={styles.topRowCol}>
                <img src={commonUtils.getImagePath('homePage/service-top-check.png')} alt='' />
                <span>{t('landing.service.topRow1')}</span>
              </div>
              <div className={styles.topRowCol}>
                <img src={commonUtils.getImagePath('homePage/service-top-check.png')} alt='' />
                <span>{t('landing.service.topRow2')}</span>
              </div>
            </div>
            <div className={styles.topRow}>
              <div className={styles.topRowCol}>
                <img src={commonUtils.getImagePath('homePage/service-top-check.png')} alt='' />
                <span>{t('landing.service.topRow3')}</span>
              </div>
              <div className={styles.topRowCol}>
                <img src={commonUtils.getImagePath('homePage/service-top-check.png')} alt='' />
                <span>{t('landing.service.topRow4')}</span>
              </div>
            </div>
          </div>
          <div className={styles.bottom}>
            <div className={styles.bottomTitle}>
              <img src={commonUtils.getImagePath('homePage/service-bottom-title.png')} alt='' />
              <span>{t('landing.service.bottomTitle')}</span>
            </div>
            <div className={styles.bottomBlock}>
              <div className={styles.blockRow}>
                <div className={styles.blockRowCol}>
                  <img src={commonUtils.getImagePath('homePage/service-bottom-check.png')} alt='' />
                  <span>{t('landing.service.bottomRow1')}</span>
                </div>
                <div className={styles.blockRowCol}>
                  <img src={commonUtils.getImagePath('homePage/service-bottom-check.png')} alt='' />
                  <span>{t('landing.service.bottomRow2')}</span>
                </div>
              </div>
              <div className={styles.blockRow}>
                <div className={styles.blockRowCol}>
                  <img src={commonUtils.getImagePath('homePage/service-bottom-check.png')} alt='' />
                  <span>{t('landing.service.bottomRow3')}</span>
                </div>
                <div className={styles.blockRowCol}>
                  <img src={commonUtils.getImagePath('homePage/service-bottom-check.png')} alt='' />
                  <span>{t('landing.service.bottomRow4')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.footerCover}></div>
        <div className={styles.footerContent}>
          <img
            className={styles.footerContentBg}
            src={commonUtils.getImagePath('homePage/footer-content-bg.png')}
            alt=''
          />
          <img
            className={styles.footerBrand}
            // src={commonUtils.getImagePath('homePage/footer-brand-mz.png')}
            src={commonUtils.getImagePath('homePage/minglue-adeff.png')}
            alt=''
          />
          <p className={styles.footerTitle}>{t('landing.footer.title')}</p>
          <p className={styles.belongDesc}>
            <a
              style={{ textDecoration: 'none', color: 'inherit' }}
              target='_blank'
              href={
                i18n.language === 'zh'
                  ? 'https://www.mininglamp.com/'
                  : 'https://www.mininglamp.com/en/'
              }
            >
              <Trans
                i18nKey='landing.footer.belongDesc'
                components={{ tmp: <span className={styles.corp} /> }}
              />
            </a>
          </p>
          <div className={styles.brandInfo}>
            <div className={styles.info}>
              <img src={commonUtils.getImagePath('homePage/footer-email.png')} alt='' />
              <span>{t('landing.footer.email')}: Adeff@mininglamp.com</span>
            </div>
          </div>
          {
            INTERNAL_HOST.includes(location.hostname) &&
            <a href="https://beian.miit.gov.cn" target='_blank' style={{ textDecoration: 'none', color: 'inherit' }}>
              <p className={styles.ba}>京ICP备09014037号-53</p>
            </a>
          }
        </div>
      </div>
      <DemoDrawer
        open={open}
        onClose={() => {
          setOpen(false)
        }}
      ></DemoDrawer>
      {userInfo === null && showAigcEntry && <AigcPromoEntry onClick={openAigcPromotionModal} />}
      <CookieAcceptNotification />
    </div>
  )
}

export default observer(HomePagePc)
